import { isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ExternalAccountLite } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { ExternalAccountLogo } from "@src/appV2/redesign/Accounts/PayoutSettings/components/ExternalAccountLogo";
import { getExpectedPayoutDate } from "@src/appV2/redesign/Payments/utils/getExpectedPayoutDate";
import { Divider, Text } from "@src/appV2/redesign/system/ui-components";

import { formatPaymentDetailsDate, formatPaymentDetailsDateWithTime } from "../utils/dates";
import { PaymentDetailsLineItem } from "./PaymentDetailsLineItem";

export interface PaymentDetailsPayoutDetailsSectionProps {
  externalAccount: ExternalAccountLite | undefined;
  isPayoutInstant: boolean;
  paymentDate: Date;
}

export function PaymentDetailsPayoutDetailsSection(props: PaymentDetailsPayoutDetailsSectionProps) {
  const { externalAccount, isPayoutInstant, paymentDate } = props;
  const expectedPayoutDate = getExpectedPayoutDate({
    paymentDate,
    isPayoutInstant,
  });

  if (isNil(externalAccount)) {
    return null;
  }

  return (
    <Stack spacing={8}>
      <Divider />
      <Text semibold variant="h6">
        Payout details
      </Text>
      <Stack spacing={6}>
        <PaymentDetailsLineItem
          iconType={externalAccount.type === "card" ? "credit-card" : "bank"}
          label="Destination"
          value={
            <Stack
              direction="row"
              sx={(theme) => ({
                alignItems: "center",
                // this maxHeight is needed to prevent the ExternalAccountLogo from making the stack too tall
                // because the ExternalAccountLogo renders a CbhIcon component which is 32x32px
                maxHeight: theme.spacing(5),
              })}
            >
              {
                // if bank, the bank icon is already rendered on the left so we skip it here
                externalAccount.type === "card" ? (
                  <ExternalAccountLogo externalAccount={externalAccount} iconSize="medium" />
                ) : null
              }
              <Text semibold variant="body2">
                ···· {externalAccount.last4}
              </Text>
            </Stack>
          }
          labelSecondLine={externalAccount.providerName}
        />
        <PaymentDetailsLineItem
          iconType="calendar-navigation"
          label="Expected delivery"
          value={
            isPayoutInstant
              ? formatPaymentDetailsDateWithTime(expectedPayoutDate)
              : formatPaymentDetailsDate(expectedPayoutDate)
          }
        />
      </Stack>
    </Stack>
  );
}
